<template>
  <v-dialog v-model="internalValue" persistent width="90vw" content-class="glass-dialog">
    <Card class="dialog-card fix-height">
      <v-toolbar height="60px" class="sub-header dialog-header elevation-0 gradient-header" color="primary">
        <div class="title font-weight-bold pl-2 pt-2">
          {{ $t("EVENT LOGS") }}
        </div>
        <v-spacer />
        <v-btn icon @click="internalValue = false">
          <v-icon large :color="isDark ? 'white' : '#000427'">close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider />

      <v-layout row justify-end px-4 pt-4 pb-2 wrap class="filter-container">
        <v-flex xs12 sm6 md2 class="pa-1">
          <v-menu ref="menu" v-model="dateMenu" :close-on-content-click="false" :nudge-right="40"
            :return-value.sync="date" lazy transition="scale-transition" offset-y full-width min-width="290px">
            <template v-slot:activator="{ on }">
              <v-text-field v-model="date" label="Date" prepend-inner-icon="event" readonly outlined dense v-on="on"
                class="rounded-input"></v-text-field>
            </template>
            <v-date-picker color="accent" v-model="date" :max="today" no-title scrollable>
              <v-spacer></v-spacer>
              <v-btn flat color="error" @click="dateMenu = false">Cancel</v-btn>
              <v-btn flat color="success" @click="$refs.menu.save(date)">OK</v-btn>
            </v-date-picker>
          </v-menu>
        </v-flex>

        <v-flex xs12 sm6 md2 class="pa-1">
          <v-menu ref="time_from_menu" v-model="timeFromMenu" :close-on-content-click="false" :nudge-right="40"
            :return-value.sync="timeFrom" lazy transition="scale-transition" offset-y full-width max-width="290px"
            min-width="290px">
            <template v-slot:activator="{ on }">
              <v-text-field v-model="timeFrom" label="Time From" prepend-inner-icon="access_time" readonly outlined
                dense v-on="on" class="rounded-input"></v-text-field>
            </template>
            <v-time-picker v-if="timeFromMenu" v-model="timeFrom" :max="timeTo" full-width header-color="#000427"
              color="accent" @click:minute="$refs.time_from_menu.save(timeFrom)"></v-time-picker>
          </v-menu>
        </v-flex>

        <v-flex xs12 sm6 md2 class="pa-1">
          <v-menu ref="time_to_menu" v-model="timeToMenu" :close-on-content-click="false" :nudge-right="40"
            :return-value.sync="timeTo" lazy transition="scale-transition" offset-y full-width max-width="290px"
            min-width="290px">
            <template v-slot:activator="{ on }">
              <v-text-field v-model="timeTo" label="Time To" prepend-inner-icon="access_time" readonly outlined dense
                v-on="on" class="rounded-input"></v-text-field>
            </template>
            <v-time-picker v-if="timeToMenu" v-model="timeTo" :max="maxTime" full-width header-color="#000427"
              color="accent" @click:minute="saveTimeHandler"></v-time-picker>
          </v-menu>
        </v-flex>

        <v-flex xs12 sm6 md2 class="pa-1" v-if="hasBothDeviceTypes">
          <v-select v-model="selectedEventFilter" :items="eventFilterOptions" label="Filter By" outlined dense
            class="rounded-input" color="accent"></v-select>
        </v-flex>

        <v-flex xs12 sm12 md4 text-xs-right class="pa-1">
          <v-btn color="accent" class="apply-btn" @click="getEventLog()" dark>
            {{ $t("APPLY") }}
          </v-btn>
        </v-flex>
      </v-layout>

      <Card class="px-4 pb-4 elevation-0 transparent-card">
        <div v-if="loading" class="text-xs-center pa-5">
          <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
        </div>
        <v-data-table v-else-if="showTable" :headers="computedHeader" :items="eventLogData"
          class="elevation-1 custom-data-table modern-table" :pagination.sync="pagination" hide-actions>
          <template v-slot:headers="props">
            <tr>
              <th v-for="header in props.headers" :key="header.text" @click="changeSort(header.value)" :class="[
                'column sortable text-xs-left',
                pagination.descending ? 'desc' : 'asc',
                header.value === pagination.sortBy ? 'active' : '',
              ]">
                {{ header.text }}
                <v-icon small v-show="header.text != ''">arrow_upward</v-icon>
              </th>
            </tr>
          </template>
          <template v-slot:items="props">
            <td class="text-xs-left pl-3">{{ props.item.timestamp }}</td>
            <td class="text-xs-left pl-3 font-weight-medium">{{ props.item.device_name }}</td>
            <td class="text-xs-left pl-3">{{ props.item.device_id }}</td>
            <td class="text-xs-left pl-3">
              <v-chip small :color="getCategoryColor(props.item.category)" text-color="white">
                {{ props.item.category ? props.item.category.toUpperCase() : '' }}
              </v-chip>
            </td>

            <template v-if="appliedEventFilter === 'on_off' || !hasBothDeviceTypes">
              <td class="text-xs-left pl-3">
                <span :class="props.item.prev_status == 'ON' ? 'success--text' : 'error--text'"
                  class="font-weight-bold">
                  {{ props.item.prev_status }}
                </span>
              </td>
              <td class="text-xs-left pl-3">
                <span :class="props.item.current_status == 'ON' ? 'success--text' : 'error--text'"
                  class="font-weight-bold">
                  {{ props.item.current_status }}
                </span>
              </td>
            </template>

            <template v-else-if="appliedEventFilter === 'params'">
              <td class="text-xs-left pl-3 font-weight-bold text-uppercase">{{ props.item.parameter }}</td>
              <td class="text-xs-left pl-3">{{ props.item.prev_value }}</td>
              <td class="text-xs-left pl-3 font-weight-bold">{{ props.item.current_value }}</td>
            </template>

            <td class="text-xs-left pl-3">
              <v-icon small :color="props.item.request_status === 'Success' ? 'success' : 'error'">
                {{ props.item.request_status === 'Success' ? 'check_circle' : 'error' }}
              </v-icon>
              {{ props.item.request_status }}
            </td>
            <td class="text-xs-left pl-3 text-truncate" style="max-width: 150px;">{{ props.item.updated_by }}</td>
          </template>
          <template v-slot:no-data>
            <div class="text-xs-center pa-4 text-muted">
              {{ $t('No events found for the selected period') }}
            </div>
          </template>
        </v-data-table>
      </Card>
    </Card>
  </v-dialog>
</template>

<script>
const moment = require("moment");
import { mapGettersMixin } from '@/util/mixin/index.js'
import Controlling from "@/services/Controlling";

export default {
  name: "EventLogDialog",
  mixins: [mapGettersMixin],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    plantId: {
      type: [Number, String],
      required: true
    },
    hasBothDeviceTypes: {
      type: Boolean,
      default: false
    },
    defaultDeviceType: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dateMenu: false,
      timeFromMenu: false,
      timeToMenu: false,
      timeFrom: moment().subtract(15, "minutes").format("HH:mm"),
      timeTo: moment().format("HH:mm"),
      date: new Date().toISOString().substr(0, 10),
      eventLogData: [],
      loading: false,
      showTable: false,
      pagination: {
        sortBy: "Logged on",
        descending: false,
        page: 1,
        rowsPerPage: 50,
      },
      selectedEventFilter: 'on_off',
      appliedEventFilter: 'on_off',
      eventFilterOptions: [
        { text: 'State', value: 'on_off' },
        { text: 'Parameters', value: 'params' }
      ]
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    today() {
      return moment().format("YYYY-MM-DD");
    },
    maxTime() {
      if (moment().isSame(this.date, "day")) {
        return moment().format("HH:mm");
      } else {
        return "23:59";
      }
    },
    computedHeader() {
      const baseHeaders = [
        { text: this.$t("Logged on"), value: "timestamp", align: "left" },
        { text: this.$t("Name"), value: "device_name", align: "left" },
        { text: this.$t("Device Id"), value: "device_id", align: "left" },
        { text: this.$t("Category"), value: "category", align: "left" },
      ];

      const specificHeaders = (this.appliedEventFilter === 'on_off' || !this.hasBothDeviceTypes)
        ? [
          { text: this.$t("Prev Status"), value: "prev_status", align: "left" },
          { text: this.$t("Current Status"), value: "current_status", align: "left" },
        ]
        : [
          { text: this.$t("Parameter name"), value: "parameter", align: "left" },
          { text: this.$t("Previous value"), value: "prev_value", align: "left" },
          { text: this.$t("Current value"), value: "current_value", align: "left" },
        ];

      const endHeaders = [
        { text: this.$t("Action"), value: "request_status", align: "left" },
        { text: this.$t("User"), value: "updated_by", align: "left" },
      ];

      return [...baseHeaders, ...specificHeaders, ...endHeaders];
    },
  },
  watch: {
    internalValue(val) {
      if (val) {
        this.getEventLog();
      }
    }
  },
  methods: {
    saveTimeHandler() {
      this.$refs.time_to_menu.save(this.timeTo);
      if (
        moment(moment(`${this.date} ${this.timeTo}`)).isBefore(
          `${this.date} ${this.timeFrom}`,
          "minutes"
        )
      ) {
        this.timeFrom = this.timeTo;
      }
    },
    getCategoryColor(category) {
      if (!category) return 'grey';
      const cat = category.toLowerCase();
      if (cat.includes('incomer')) return 'blue darken-2';
      if (cat.includes('feeder')) return 'teal darken-1';
      return 'blue-grey';
    },
    changeSort(column) {
      if (column != "") {
        if (this.pagination.sortBy === column) {
          this.pagination.descending = !this.pagination.descending;
        } else {
          this.pagination.sortBy = column;
          this.pagination.descending = false;
        }
      }
    },
    async getEventLog() {
      this.appliedEventFilter = this.selectedEventFilter;
      this.showTable = false;
      this.loading = true;
      try {
        const timeFrom = moment(`${this.date} ${this.timeFrom}`).format("X");
        const timeTo = moment(`${this.date} ${this.timeTo}`).format("X");

        const deviceType =
          this.selectedEventFilter === "on_off"
            ? "breaker"
            : this.selectedEventFilter === "params"
              ? "relay"
              : this.defaultDeviceType || "breaker";

        const response = await Controlling.fetchEventLog(
          this.plantId,
          deviceType,
          timeFrom,
          timeTo
        );

        if (response && response.data && response.data.message) {
          this.eventLogData = _.flatten(
            response.data.message.map((el) => this.processLogEntry(el))
          );
        } else {
          this.eventLogData = [];
        }
        this.showTable = true;
      } catch (error) {
        console.error("Error fetching event log:", error);
        this.eventLogData = [];
        this.showTable = true;
      } finally {
        this.loading = false;
      }
    },
    processLogEntry(el) {
      const base = {
        timestamp: moment.unix(el.timestamp).format("YYYY-MM-DD HH:mm"),
        device_id: el.device_id,
        device_name: el.device_name,
        category: el.category,
        updated_by: el.updated_by,
      };

      if (el.device_type !== "relay") {
        return [
          {
            ...base,
            prev_status: el.prev_status == 1 ? "ON" : "OFF",
            current_status: el.current_status == 1 ? "ON" : "OFF",
            request_status: this.formatStatus(el.request_status),
          },
        ];
      }

      if (!el.parameter) return [];

      return Object.keys(el.parameter).map((name) => ({
        ...base,
        parameter: name,
        prev_value: el.parameter[name].prev_value,
        current_value: el.parameter[name].current_value,
        request_status: this.formatStatus(el.parameter[name].request_status),
      }));
    },
    formatStatus(status) {
      if (!status) return "";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
  }
};
</script>

<style scoped>
.modern-table {
  border-radius: 8px;
  overflow: hidden;
}

.modern-table thead th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
}

.theme--dark .modern-table thead th {
  background-color: #1e272e;
  color: #fff;
}

.theme--light .rounded-input {
  color: rgb(27, 27, 27);
}

.fix-height {
  height: 90vh !important;
}

.filter-container {
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.theme--dark .filter-container {
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
</style>
